import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Copy, Trash2, RefreshCw, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const InvitationActions = ({ invitation, onCopy, onDelete, onResend }) => {
  const [loading, setLoading] = useState({
    copy: false,
    delete: false,
    resend: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const { currentUser } = useAuth();

  const isExpired = (expiresAt) => {
    return new Date() > expiresAt.toDate();
  };

  const getActualStatus = () => {
    const expired = isExpired(invitation.expiresAt);
    return expired && invitation.status === 'pending' ? 'expired' : invitation.status;
  };

  const handleCopyLink = async () => {
    setLoading(prev => ({ ...prev, copy: true }));
    setError('');
    setSuccess('');

    try {
      const invitationLink = `${window.location.origin}/register?invitation=${invitation.token}`;
      await navigator.clipboard.writeText(invitationLink);
      
      setSuccess('Enlace copiado al portapapeles');
      
      if (onCopy) {
        onCopy(invitation.token, invitationLink);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      setError('Error al copiar el enlace');
      
      // Fallback: show the link in a prompt
      const invitationLink = `${window.location.origin}/register?invitation=${invitation.token}`;
      prompt('Copie este enlace manualmente:', invitationLink);
    } finally {
      setLoading(prev => ({ ...prev, copy: false }));
    }
  };

  const handleDeleteInvitation = async () => {
    setLoading(prev => ({ ...prev, delete: true }));
    setError('');
    setSuccess('');
    setShowDeleteConfirmation(false);

    try {
      await deleteDoc(doc(db, 'invitations', invitation.id));
      
      setSuccess('Invitación eliminada exitosamente');
      
      if (onDelete) {
        onDelete(invitation.id);
      }
      
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setError('Error al eliminar la invitación');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const handleResendInvitation = async () => {
    setLoading(prev => ({ ...prev, resend: true }));
    setError('');
    setSuccess('');
    setShowResendConfirmation(false);

    try {
      // Check if there's already an active invitation for this email
      const q = query(
        collection(db, 'invitations'),
        where('email', '==', invitation.email),
        where('status', '==', 'pending')
      );
      const existingInvitations = await getDocs(q);
      
      // Delete existing pending invitations for this email
      const deletePromises = existingInvitations.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      // Generate new token
      const newToken = generateInvitationToken();
      
      // Create new invitation
      await addDoc(collection(db, 'invitations'), {
        email: invitation.email,
        role: invitation.role,
        token: newToken,
        status: 'pending',
        createdAt: new Date(),
        createdBy: currentUser.uid,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        usedAt: null,
        isResent: true,
        originalInvitationId: invitation.id
      });

      setSuccess('Nueva invitación enviada con token actualizado');
      
      if (onResend) {
        onResend(invitation.email, newToken);
      }
      
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Error al reenviar la invitación');
    } finally {
      setLoading(prev => ({ ...prev, resend: false }));
    }
  };

  const generateInvitationToken = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'used':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const actualStatus = getActualStatus();
  const expired = isExpired(invitation.expiresAt);
  const canCopy = invitation.status === 'pending' && !expired;
  const canResend = actualStatus === 'expired' || invitation.status === 'pending';
  const canDelete = invitation.status !== 'used';

  return (
    <>
      <div className="flex items-center space-x-2">
        {/* Success/Error Messages */}
        {success && (
          <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
            {success}
          </div>
        )}
        
        {error && (
          <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-1">
          {/* Copy Link Button */}
          {canCopy && (
            <button
              onClick={handleCopyLink}
              disabled={loading.copy}
              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded disabled:opacity-50"
              title="Copiar enlace de invitación"
            >
              {loading.copy ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Resend Button */}
          {canResend && (
            <button
              onClick={() => setShowResendConfirmation(true)}
              disabled={loading.resend}
              className="text-orange-600 hover:text-orange-900 p-1 hover:bg-orange-50 rounded disabled:opacity-50"
              title="Reenviar invitación con nuevo token"
            >
              {loading.resend ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Delete Button */}
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={loading.delete}
              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded disabled:opacity-50"
              title="Eliminar invitación"
            >
              {loading.delete ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                <h3 className="text-lg font-medium text-gray-900">Confirmar Eliminación</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  ¿Está seguro de eliminar esta invitación?
                </p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="font-medium text-gray-900">{invitation.email}</div>
                  <div className="text-gray-600">Rol: {invitation.role}</div>
                  <div className="text-gray-600">Estado: {actualStatus}</div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Esta acción no se puede deshacer.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteInvitation}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend Confirmation Dialog */}
      {showResendConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <RefreshCw className="h-6 w-6 text-orange-500" />
                <h3 className="text-lg font-medium text-gray-900">Confirmar Reenvío</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  ¿Está seguro de reenviar esta invitación?
                </p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="font-medium text-gray-900">{invitation.email}</div>
                  <div className="text-gray-600">Rol: {invitation.role}</div>
                  <div className="text-gray-600">Estado actual: {actualStatus}</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <div className="text-xs text-yellow-700">
                      <p className="font-medium">Esto creará una nueva invitación con:</p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>Nuevo token de seguridad</li>
                        <li>Nueva fecha de expiración (7 días)</li>
                        <li>Eliminará la invitación anterior</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowResendConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleResendInvitation}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  Reenviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvitationActions;